import React, { FC, useCallback } from 'react';
import ImageGallery from 'react-image-gallery';
import _orderBy from 'lodash/orderBy';
import { gqlSubmissionMedia, gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { CoreTheme as theme } from '@minecraft.themes';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import { useTranslation } from '@blocs.i18n';
import { ImageGalleryWrapper } from '../QuickViewOverlayStyles';
import {
  ContentWrapper,
  Title,
  SubmissionNoteContainer,
  SubmissionNote,
  StyledCloudinaryPhoto,
  StyledArrowButton,
  ThickIcon,
} from './QuickViewOverlayContentStyles';

type Props = {
  submissionNote: string;
  submissionRequestedMedia: gqlSubmissionMedia[];
};

export const getSubmittedMedia = (medias: gqlSubmissionMedia[]): gqlSubmissionMedia[] => {
  const filteredMedia = _orderBy(medias, ['isPrimary', 'order'], ['desc', 'asc']);

  const mediaByType = filteredMedia.reduce(
    (acc, file) => {
      const type = file.mediaType.code;

      return {
        ...acc,
        [type]: [
          ...acc[type],
          {
            media: {
              ...file,
              withDownloadLink: true,
            },
          },
        ],
      };
    },
    {
      [MEDIA_TYPE.PHOTO]: [],
      [MEDIA_TYPE.VIDEO]: [],
      [MEDIA_TYPE.AUDIO]: [],
    }
  );

  return [...mediaByType[MEDIA_TYPE.PHOTO], ...mediaByType[MEDIA_TYPE.VIDEO], ...mediaByType[MEDIA_TYPE.AUDIO]];
};

const QuickViewOverlayContent: FC<Props> = ({ submissionNote, submissionRequestedMedia }) => {
  const { t } = useTranslation();
  const renderMediaItem = useCallback(({ media }) => {
    const { mediaType, mediaStatus, fileKey, mediaStorageStatus, thumbnailUrl, thumbnail, transformation, url } = media;

    if (mediaType.code === MEDIA_TYPE.PHOTO) {
      return (
        <StyledCloudinaryPhoto
          mediaStorageStatus={mediaStorageStatus}
          transformation={transformation}
          thumbnail={thumbnail}
          fileKey={fileKey}
          type="lg"
          url={url}
        />
      );
    }

    return (
      <CloudinaryVideoAudio
        fileKey={fileKey}
        url={url}
        mediaType={mediaType.code}
        mediaStatus={mediaStatus.code}
        mediaStorageStatus={mediaStorageStatus.code}
        thumbnailUrl={thumbnailUrl}
        thumbnail={thumbnail}
        type="lg"
      />
    );
  }, []);

  const renderLeftNav = useCallback((onClick, disabled) => {
    return (
      <StyledArrowButton $left onClick={onClick} disabled={disabled}>
        <ThickIcon name="chevron-left-thick" color={theme.colors.primary.white} />
      </StyledArrowButton>
    );
  }, []);

  const renderRightNav = useCallback((onClick, disabled) => {
    return (
      <StyledArrowButton $right onClick={onClick} disabled={disabled}>
        <ThickIcon name="chevron-right-thick" color={theme.colors.primary.white} />
      </StyledArrowButton>
    );
  }, []);

  const currentSlides = getSubmittedMedia(submissionRequestedMedia);

  return (
    <ContentWrapper>
      {submissionRequestedMedia?.length > 0 && (
        <ImageGalleryWrapper>
          <Title>{t('casting:talent.submission.mediaCount', { count: submissionRequestedMedia?.length })}</Title>
          <ImageGallery
            items={currentSlides}
            showFullscreenButton={false}
            showIndex
            showThumbnails={false}
            showPlayButton={false}
            slideDuration={250}
            startIndex={0}
            renderItem={renderMediaItem}
            renderLeftNav={renderLeftNav}
            renderRightNav={renderRightNav}
          />
        </ImageGalleryWrapper>
      )}
      {Boolean(submissionNote) && (
        <SubmissionNoteContainer>
          <Title>{t('common:profileEdit.submissionNote')}</Title>
          <SubmissionNote>{submissionNote}</SubmissionNote>
        </SubmissionNoteContainer>
      )}
    </ContentWrapper>
  );
};

export default QuickViewOverlayContent;
